/****************************************************
 * Module Name:  utility.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import Constants from "./constants";
import * as React from 'react';
import { Chip, TextField, FormControl, Autocomplete } from "@mui/material";
import { getGridStringOperators, getGridSingleSelectOperators, GridFilterInputValue, useGridApiRef } from "@mui/x-data-grid-pro";
import Select from "react-select";
import CloseIcon from '@mui/icons-material/Close';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export const getDropdownEditComponent = (options, openDropdown, setOpenDropdown, isMulti) => {
  if (!openDropdown) {
    setTimeout(() => setOpenDropdown(true), 0);
  }
  const formattedOptions = options.map(opt => typeof opt === "string" ? { id: opt, label: opt } : opt);

  return function DropdownEditComponent(params) {
    const rawValues = isMulti
      ? (Array.isArray(params.value) ? params.value : (params.value || '').split(',').map(val => val.trim()))
      : [params.value ? params.value.trim() : '' || ''];

    const valueArray = rawValues
      .map(label => formattedOptions.find(opt => opt.label === label))
      .filter(Boolean);

    const finalValue = isMulti ? valueArray : valueArray[0] || null;

    return (
      <Autocomplete
        multiple={isMulti}
        autoFocus
        fullWidth
        disableClearable
        disableCloseOnSelect
        openOnFocus
        options={formattedOptions}
        value={finalValue}
        open={openDropdown}
        onOpen={() => setOpenDropdown(true)}
        onClose={() => setOpenDropdown(false)}
        onChange={(e, newValue) => {
          const valueString = Array.isArray(newValue)
            ? newValue.map(v => v.label).join(',')
            : newValue.label || '';
          params.api.setEditCellValue({ id: params.id, field: params.field, value: valueString }, e);
          params.api.stopCellEditMode({ id: params.id, field: params.field });
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => option.label || ''}
        renderInput={(inputParams) => (
          <TextField
            {...inputParams}
            variant="standard"
            size="small"
            InputProps={{
              ...inputParams.InputProps,
              style: {
                minHeight: 36,
                maxHeight: 36,
                overflow: 'hidden',
                alignItems: 'center',
              }
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          isMulti && (
            <div
              ref={(el) => {
                if (el) {
                  el.scrollLeft = el.scrollWidth;
                }
              }}
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                overflowX: 'auto',
                overflowY: 'hidden',
                width: '100%',
                maxHeight: '36px',
                alignItems: 'center',
                paddingBottom: 5,
              }}
            >
              {value.map((option, index) => (
                <Chip
                  key={option.id}
                  label={option.label}
                  {...getTagProps({ index })}
                  size="small"
                  style={{
                    background: '#e0e0e0',
                    height: 22,
                    fontSize: '0.7rem',
                    marginTop: '9px',
                  }}
                />
              ))}
            </div>
          )
        }

        ListboxProps={{
          style: {
            maxHeight: 200,
            overflowY: 'auto',
          },
        }}
      />
    );
  };
};
export default class Utility {

  static useSingleClickEdit = () => {
    const apiRef = useGridApiRef();

    const handleCellClick = React.useCallback((params) => {
      const { colDef, id, field } = params;
      if (!colDef.editable) return;

      if (!(apiRef.current.getCellMode(id, field) === "edit"))
        apiRef.current.startCellEditMode({ id, field });
    }, []);

    return { apiRef, handleCellClick };
  }

  static ConvertNewLineToComma = (inString) => {
    return inString ? inString.replace(/(?:\r\n|\r|\n)/g, ",") : "";
  };

  static combinedFilters = () => {
    return [
      ...getGridSingleSelectOperators(),
      ...getGridStringOperators().filter(operator => operator.value !== 'isAnyOf'),
    ]
  };

  static customFilters = (filterMap, field, id, username) => {
    return [
      {
        label: 'contains',
        value: 'contains',
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.value) {
            return null;
          }

          const searchValue = filterItem.value.trim().toLowerCase();

          return ({ row }) => {
            const arr = row[field] || [];

            if (Array.isArray(arr)) {
              return arr.some(
                (item) =>
                  item[id].toString().toLowerCase().trim().includes(searchValue) ||
                  item[username].toLowerCase().trim().includes(searchValue)
              );
            } else {
              return arr.toString().toLowerCase().trim().includes(searchValue) ||
                row[id].toString().toLowerCase().trim().includes(searchValue)
            }
          };
        },
        InputComponent: ({ item, applyValue }) => (
          <TextField
            fullWidth
            label="Value"
            value={item.value || ''}
            onChange={(event) =>
              applyValue({ ...item, value: event.target.value || null })
            }
            variant="outlined"
            size="small"
            sx={{
              marginTop: '9px',
              marginLeft: 1,
              height: "49px",
              width: '11rem',
            }}
          />
        ),
      },
      {
        label: 'is empty',
        value: 'isEmpty',
        getApplyFilterFn: () => {
          return ({ row }) => {
            const arr = row[field] || [];
            return arr.length === 0;
          };
        },
        InputComponent: () => <div />,
      },
      {
        label: 'is not empty',
        value: 'isNotEmpty',
        getApplyFilterFn: () => {
          return ({ row }) => {
            const arr = row[field] || [];
            return arr.length !== 0;
          };
        },
        InputComponent: () => <div />,
      },
      {
        label: 'is',
        value: 'is',
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.value) {
            return null;
          }

          const selectedUsername = filterItem.value.split("|")[1].trim();

          return ({ value, row, column, apiRef }) => {
            const arr = row[field] || [];

            if (Array.isArray(arr)) {
              return arr.some(
                (a) => a[username].trim() === selectedUsername
              );
            } else {
              return arr.trim() === selectedUsername;
            }
          };
        },
        InputComponent: ({ item, applyValue }) => {
          const handleChange = (selectedOption) => {
            const newValue = selectedOption ? selectedOption.value : null;
            applyValue({ ...item, value: newValue });
          };

          const options = (filterMap && filterMap[field])
            ? filterMap[field].map((val) => ({ label: val, value: val }))
            : [];

          return (
            <FormControl fullWidth sx={{ marginLeft: 1, position: 'relative' }}>
              <Select
                options={options}
                value={options.find((opt) => opt.value === item.value) || null}
                onChange={handleChange}
                isClearable
                placeholder="Select..."
                menuPortalTarget={document.body}
                styles={{
                  control: (base) => ({
                    ...base,
                    marginTop: '8px',
                    minHeight: '40px',
                    width: '11rem',
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </FormControl>
          );
        },
        InputComponentProps: {
          type: 'singleSelect',
          valueOptions: (filterMap && filterMap[field])
            ? filterMap[field]
            : [],
        },
      },
      {
        label: 'is not',
        value: 'isNot',
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.value) {
            return null;
          }

          const selectedUsername = filterItem.value.split("|")[1].trim();

          return ({ row }) => {
            const arr = row[field] || [];
            if (Array.isArray(arr)) {
              return !arr.some((a) => a[username].trim() === selectedUsername);
            }
            else {
              return arr.trim() !== selectedUsername;
            }
          };
        },
        InputComponent: ({ item, applyValue }) => {
          const handleChange = (selectedOption) => {
            const newValue = selectedOption ? selectedOption.value : null;
            applyValue({ ...item, value: newValue });
          };

          const options = (filterMap && filterMap[field])
            ? filterMap[field].map((val) => ({ label: val, value: val }))
            : [];

          return (
            <FormControl fullWidth sx={{ marginLeft: 1, position: 'relative' }}>
              <Select
                options={options}
                value={options.find((opt) => opt.value === item.value) || null}
                onChange={handleChange}
                isClearable
                placeholder="Select..."
                menuPortalTarget={document.body}
                styles={{
                  control: (base) => ({
                    ...base,
                    marginTop: '8px',
                    minHeight: '40px',
                    width: '11rem',
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </FormControl>
          );
        },
        InputComponentProps: {
          type: 'singleSelect',
          valueOptions: (filterMap && filterMap[field])
            ? filterMap[field]
            : [],
        },
      },
      {
        label: 'is any of',
        value: 'isAnyOf',
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.value || !Array.isArray(filterItem.value) || filterItem.value.length === 0) {
            return () => true;
          }

          const selectedUsernames = filterItem.value.map((val) => val.split("|")[1].trim());

          return ({ row }) => {
            const arr = row[field] || [];
            if (Array.isArray(arr)) {
              return arr.some((a) => selectedUsernames.includes(a[username].trim()));
            }
            else {
              return selectedUsernames.includes(arr.trim());
            }
          };
        },
        InputComponent: ({ item, applyValue }) => {
          const handleSelectChange = (selectedOptions) => {
            const newValue = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
            applyValue({ ...item, value: newValue });
          };

          const handleDelete = (valToDelete, event) => {
            const newValue = (item.value || []).filter((val) => val !== valToDelete);
            applyValue({ ...item, value: newValue });
          };

          const options = filterMap[field].map((val) => ({ label: val, value: val, })) || [];

          return (
            <FormControl fullWidth sx={{ marginLeft: 1, position: 'relative' }}>
              <Select
                isMulti
                options={options}
                value={options.filter((opt) => (item.value || []).includes(opt.value))}
                onChange={handleSelectChange}
                placeholder="Select..."
                closeMenuOnSelect={false}
                menuPortalTarget={document.body}

                styles={{
                  control: (base) => ({
                    ...base,
                    marginTop: '8px',
                    minHeight: '40px',
                    marginRight: '15px',
                  }),
                  indicatorSeparator: (base) => ({
                    display: 'none', // Ensures the dropdown indicator section and vertical line is hidden
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem',
                  }),
                  multiValue: (base) => ({
                    ...base,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '4px 8px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '4px',
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    cursor: 'pointer',
                    color: 'black',
                    ':hover': { color: 'black' },
                  }),
                  menu: (base) => ({
                    ...base,
                    fontSize: '15px', // Decrease font size of the dropdown options
                  }),
                }}
                components={{
                  DropdownIndicator: () => null,
                  MultiValueContainer: ({ children, data, removeProps }) => (
                    <Chip
                      key={data.value}
                      label={data.label}
                      onDelete={() => handleDelete(data.value)}
                      deleteIcon={
                        <CloseIcon
                          sx={{
                            fontSize: '5px',
                            color: 'black',
                          }}
                        />
                      }
                      onMouseDown={(event) => event.stopPropagation()}
                      sx={{
                        marginTop: '4px',
                        height: 26,
                        fontSize: '0.7rem',
                        '& .MuiChip-deleteIcon': {
                          color: 'black',
                          fontSize: '15px',
                        },
                      }}

                    />
                  ),
                }}
              />
            </FormControl>
          );
        },
        InputComponentProps: {
          type: 'singleSelect',
          valueOptions: (filterMap && filterMap[field])
            ? filterMap[field]
            : [],
        },
      },
    ];
  };

  static getDropDownComponent = (options, openDropdown, setOpenDropdown, isMulti) => {
    return getDropdownEditComponent(options, openDropdown, setOpenDropdown, isMulti);
  }

  // these custom filter are used for the columns which has both string and number values
  static defaultFilters = () => {
    const stringOperators = getGridStringOperators().filter(operator =>
      ['isEmpty', 'isNotEmpty', 'isAnyOf'].includes(operator.value)
    );
    const combinedFilterOperators = [...this.customFilterOperators, ...stringOperators];
    return combinedFilterOperators;
  }

  static customFilterOperators = [
    {
      label: '=',
      value: 'equals',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null;
        return ({ value }) => Number(value) === Number(filterItem.value);
      },
      InputComponent: GridFilterInputValue,
    },
    {
      label: '!=',
      value: 'notEquals',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null;
        return ({ value }) => Number(value) !== Number(filterItem.value);
      },
      InputComponent: GridFilterInputValue,
    },
    {
      label: '>=',
      value: 'greaterThanOrEqual',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null;
        return ({ value }) => Number(value) >= Number(filterItem.value);
      },
      InputComponent: GridFilterInputValue,
    },
    {
      label: '>',
      value: 'greaterThan',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null;
        return ({ value }) => Number(value) > Number(filterItem.value);
      },
      InputComponent: GridFilterInputValue,
    },
    {
      label: '<=',
      value: 'lessThanOrEqual',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null;
        return ({ value }) => Number(value) <= Number(filterItem.value);
      },
      InputComponent: GridFilterInputValue,
    },
    {
      label: '<',
      value: 'lessThan',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null;
        return ({ value }) => Number(value) < Number(filterItem.value);
      },
      InputComponent: GridFilterInputValue,
    },
    {
      label: 'Contains',
      value: 'contains',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null;
        return ({ value }) => String(value).toLowerCase().includes(String(filterItem.value).toLowerCase());
      },
      InputComponent: GridFilterInputValue,
    },
    {
      label: 'Starts With',
      value: 'startsWith',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null;
        return ({ value }) => String(value).toLowerCase().startsWith(String(filterItem.value).toLowerCase());
      },
      InputComponent: GridFilterInputValue,
    },
    {
      label: 'Ends With',
      value: 'endsWith',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null;
        return ({ value }) => String(value).toLowerCase().endsWith(String(filterItem.value).toLowerCase());
      },
      InputComponent: GridFilterInputValue,
    },
  ];

  static isValueValid = (value) => {
    return value !== null && value !== undefined && value !== '';
  }

  static getDistinctOptions = (responseData, field) => {
    if (!responseData || !field) {
      return [];
    }
    responseData = Array.isArray(responseData) ? responseData : [responseData];
    const values = responseData.map(item => item[field]);

    return [...new Set(values)];
  };

  static getPermissionsChange = (initialStoneType, newStoneType) => {
    const initialStoneTypeIds = initialStoneType.map(stone => stone.value);
    const newStoneTypeIds = newStoneType.map(stone => stone.value);

    if (JSON.stringify(initialStoneTypeIds) === JSON.stringify(newStoneTypeIds)) {
      return { permissionsToRemove: [], permissionsToAdd: [] };
    }
    const permissionsToRemove = initialStoneTypeIds.filter(permissionId => !newStoneTypeIds.includes(permissionId));
    const permissionsToAdd = newStoneTypeIds.filter(permissionId => !initialStoneTypeIds.includes(permissionId));

    return { permissionsToRemove: permissionsToRemove, permissionsToAdd: permissionsToAdd };
  };

  // this custom sorting is for sorting the data by (A-Z and then numbers)
  static sortFn = (response, sortBy) => {
    response.sort((a, b) => {
      const nameA = a[sortBy] || "";
      const nameB = b[sortBy] || "";

      const isAlphaA = /^[a-zA-Z]/.test(nameA);
      const isAlphaB = /^[a-zA-Z]/.test(nameB);

      if (isAlphaA && !isAlphaB) return -1;
      if (!isAlphaA && isAlphaB) return 1;
      return nameA.localeCompare(nameB);
    });
    return response;
  };

  // this custom sort function is used for sorting
  static sortOperatorFn = (v1, v2, feild) => {
    const isEmpty1 = v1.length === 0;
    const isEmpty2 = v2.length === 0;

    if (isEmpty1 && !isEmpty2) return 1;
    if (!isEmpty1 && isEmpty2) return -1;

    if (isEmpty1 && isEmpty2) return 0;

    const username1 = Array.isArray(v1) ? v1[0][feild].toLowerCase() : v1 || '';
    const username2 = Array.isArray(v2) ? v2[0][feild].toLowerCase() : v2 || '';
    return username1.localeCompare(username2);
  };

  static sortAlphaNumeric = (a, b) => {
    const strA = String(a);
    const strB = String(b);

    const isNumA = !isNaN(a);
    const isNumB = !isNaN(b);

    // Numbers first
    if (isNumA && isNumB) return a - b;
    if (isNumA) return -1;
    if (isNumB) return 1;

    return strA.localeCompare(strB, undefined, { numeric: true });
  };

  static GetDateFromString = (dtString) => {
    const d = new Date(dtString);
    return (
      d.getDay() +
      "-" +
      d.getMonth() +
      "-" +
      d.getFullYear() +
      " " +
      d.getHours() +
      ":" +
      d.getMinutes()
    );
  };

  static GetFormatedDisplayDate = (cellValue) => {
    let dt = new Date(Date.parse(cellValue));
    return (
      dt.getDate() +
      "-" +
      Constants.MONTHS[dt.getMonth()] +
      "-" +
      dt.getFullYear()
    );
  };

  static GetFormatedDBDate = (cellValue) => {
    let dt = new Date(Date.parse(cellValue));
    const m = dt.getMonth() + 1;
    const d = dt.getDate();

    return (
      dt.getFullYear() +
      "-" +
      (m < 10 ? "0" + m : m) +
      "-" +
      (d < 10 ? "0" + d : d)
    );
  };

  static GetUTCTime = () => {
    return new Date().toISOString();
  };

  static ConvertLocalTimeToUtc = (date) => {
    return new Date(date).toISOString();
  };

  static ConvertUtcToLocalTime = (cellValue) => {
    if (!cellValue || cellValue === null) return "";

    let theDate = new Date(Date.parse(cellValue));
    return (
      theDate.toLocaleDateString("en-GB") + " " + theDate.toLocaleTimeString()
    );
  };

  static ConvertUtcToLocalDate = (cellValue) => {
    if (!cellValue || cellValue === null) return "";

    let theDate = new Date(Date.parse(cellValue));
    return theDate.toLocaleDateString("en-GB");
  };

  static CheckIsEmail = (value) => {
    let validEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return validEmailRegex.test(value);
  };

  static CheckIsAlphaNumeric = (value) => {
    let validRegex = /^(?=.*[a-zA-Z])(?=.*[0-9])[A-Za-z0-9]+$/;
    return value.match(validRegex);
  };

  static CheckIsPanNumber = (num) => {
    let validPanNoRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return num.match(validPanNoRegex)
  }

  static CheckIsNumber = (value) => {
    return !isNaN(value); // isNaN will check if it is not a number so when it return true we return false to identify it as a number
  };

  static CheckIsString = (value) => {
    return isNaN(value);
  };

  static CheckIsPhone = (input_str) => {
    let validPhoneRegex = /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/;
    return validPhoneRegex.test(input_str);
  };

  static CheckisNumberGreaterThanZero = (value) => {
    return this.CheckIsNumber(value) && value > 0;
  };

  static CheckisNumberPositive = (value) => {
    return this.CheckIsNumber(value) && value >= 0;
  };

  static CheckisNumberNegative = (value) => {
    return this.CheckIsNumber(value) && value <= 0;
  };

  static CheckIfNumberNegative = (value) => {
    return this.CheckIsNumber(value) && value < 0;
  };

  static CheckIsPresent = (value) => {
    return !(value === null || value === undefined || value.trim() === "");
  };

  static CheckIsNumberAndHyphen = (value) => {
    let validRegex = /^[0-9-]+$/;
    return validRegex.test(value);
  };

  static IsToGreaterThanFrom(fromValue, toValue) {
    const from = parseFloat(fromValue);
    const to = parseFloat(toValue);

    if (isNaN(from) || isNaN(to)) {
      return false;
    }

    return to >= from;
  }


  static isReadAllowed = (allPermissions) => {
    if (!allPermissions) return false;

    if (allPermissions.length > 0) return true;

    return false;
  };

  static isAddAllowed = (allPermissions) => {
    if (!allPermissions) return false;

    return (
      allPermissions.find((iPermission) => iPermission.type === "ADD") !==
      undefined
    );
  };

  static isUpdateAllowed = (allPermissions) => {
    if (!allPermissions) return false;

    return (
      allPermissions.find((iPermission) => iPermission.type === "UPDATE") !==
      undefined
    );
  };

  static isDeleteAllowed = (allPermissions) => {
    if (!allPermissions) return false;

    return (
      allPermissions.find((iPermission) => iPermission.type === "DELETE") !==
      undefined
    );
  };

  static GenerateUniqueIdOfResponseForDataGrid = (listData) => {
    let uniqueId = 0;
    let newArr = [];
    listData.map((iList) => {
      uniqueId++;
      newArr.push({ ...iList, uniqueId: uniqueId });
      return true;
    });
    return newArr;
  };

  static ReloadSite = (url) => {
    //Resetting all the global variable - Just in Case
    Constants.loggedInUser = undefined;
    Constants.userToken = undefined;
    Constants.USER_PERMISSION = undefined;

    //Actually reloading a Site
    if (url) {
      window.location.href = url;
    } else {
      window.location.reload(true);
    }

  };

  static ParseAndGetJWTExpTime = (jwtToken) => {
    return (JSON.parse(atob(jwtToken.split('.')[1]))).exp
  }

  static GetValueArrayWithoutStar = (selectedOptions) => {
    let newArr = [];
    selectedOptions.map((option) => {
      if (option.value !== "*") newArr.push(option.value);
      return true;
    });
    return newArr;
  };

  static GetValueArray = (selectedOptions) => {
    return selectedOptions.map((option) => option.value);
  };

  static GetValueArrayWithStarReplace = (selectedOptions, starReplaceValue) => {
    let newArr = this.GetValueArray(selectedOptions);
    return newArr.includes("*") ? starReplaceValue : newArr;
  };

  static GetRemark = (data) => {
    if (data === null) {
      return []
    }
    let rr = data.split(' --|-- ')
    let arr = []
    rr.map((item, idx) => {
      arr.push({
        category: item.split('|')[1],
        remark: item.split('|')[2],
        id: idx + 1
      })
      return true
    })
    return arr;
  };

  static GetRandomColorForPieChart = () => {
    return Constants.CHART_COLORS[
      Math.floor(Math.random() * Constants.CHART_COLORS.length)
    ];
  };

  static GetRandomColorForBarChart = () => {
    return Constants.BAR_COLORS[
      Math.floor(Math.random() * Constants.BAR_COLORS.length)
    ];
  };

  static StringToIntegerArray = (values) => {
    let newvalue = []
    values.map((ival) => {
      newvalue.push(Number(ival))
      return true
    })
    return newvalue
  }

  static NumberToLocaleString = (value) => {
    if (value === "" || value === null) return "";
    return value.toLocaleString('en-IN');
  }

  static ConvertNumberWithSpecifiedDigits = (value) => {
    if (value !== null && value !== undefined && value !== '' && typeof value === 'number')
      return value.toFixed(Constants.DEFAULT_NO_OF_DIGITS);

    value = 0;
    return value.toFixed(Constants.DEFAULT_NO_OF_DIGITS);
  }

  static GetBuyOrSell = (plural) => {
    return Constants.loggedInUser.usertype === 'E' ? (plural === undefined || plural === false ? 'Sell' : 'Sales') : 'Buy';
  }

  static NeedToShowStoneDetailModal = () => {
    if (
      Constants.loggedInUser.usertype === 'B' ||
      Constants.loggedInUser.usertype === 'C' ||
      ((Constants.loggedInUser.usertype === 'E') &&
        (Constants.USER_PERMISSION.find((iPermission) => iPermission.permissionid === Constants.PERMISSIONS.STONEDETAIL) === undefined) &&
        (Constants.USER_PERMISSION.find((iPermission) => iPermission.permissionid === Constants.PERMISSIONS.STONE && iPermission.type === Constants.PERMISSION_TYPE[1]) === undefined))
    )
      return false;
    else
      return true;
  }

  static NeedToShowBucketDetailModal = () => {
    if ((Constants.USER_PERMISSION.find((iPermission) => iPermission.permissionid === Constants.PERMISSIONS.BUCKETDETAIL) === undefined))
      return false;
    else
      return true;
  }

  static NeedToShowKapanDetailModal = () => {
    if ((Constants.USER_PERMISSION.find((iPermission) => iPermission.permissionid === Constants.PERMISSIONS.KAPANDETAIL) === undefined))
      return false;
    else
      return true;
  }

  static NeedToShowPacketDetailModal = () => {
    if ((Constants.USER_PERMISSION.find((iPermission) => iPermission.permissionid === Constants.PERMISSIONS.MANUFACTURING_PACKETDETAIL) === undefined))
      return false;
    else
      return true;
  }

  static ConvertDateToAge = (value) => {
    if (value !== null) {
      const stoneCreationDate = new Date(value);
      const currentDate = new Date();

      const differenceInMilliseconds = currentDate - stoneCreationDate;
      const ageInDays = Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));
      const age = Math.max(ageInDays, 0);

      return age;
    }
    return null;
  }

  static PrepareColumns = (columnsData) => {
    let columnNames = [];

    for (const key in columnsData) {
      if (key.startsWith("item-")) {
        columnNames.push(columnsData[key]);
      }
      else break;
    }

    Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
      if (columnsData.hasOwnProperty(iColPermission)) {
        columnNames.push(columnsData[iColPermission]);
      }
    });
    return columnNames;
  };

  static getChangedFields = (newRow, oldRow) => {
    const changedFields = [];

    Object.keys(newRow).forEach(field => {
      if (newRow[field] !== oldRow[field]) {
        changedFields.push({
          id: newRow.stoneid,
          field: field,
          value: typeof newRow[field] === 'string' ? newRow[field].trim() : newRow[field]
        });
      }
    });

    return changedFields;
  };

  static handleColumnOrderChange = (params, columns, setColumns, route) => {
    const { targetIndex, oldIndex } = params;

    if (oldIndex < 0 || targetIndex < 0 || oldIndex >= columns.length || targetIndex >= columns.length) {
      return;
    }

    const newColumns = [...columns];
    const [movedColumn] = newColumns.splice(oldIndex - 1, 1);
    newColumns.splice(targetIndex - 1, 0, movedColumn);
    setColumns(newColumns);

    let columnOrder = [];
    newColumns.map(col => columnOrder.push(col.field))

    const routeSegments = route.split('/');
    const routeKey = routeSegments[routeSegments.length - 1];
    const localStorageKey = `${routeKey}_ColumnOrder`;

    localStorage.setItem(localStorageKey, JSON.stringify(columnOrder));
  };

  static applySavedColumnOrder = (savedColumnOrder, originalColumns, setColumns) => {
    if (savedColumnOrder) {
      const reorderedColumns = savedColumnOrder.map(savedColumn => {
        const originalColumn = originalColumns.find(column => column.field === savedColumn);
        return { ...originalColumn };
      });
      setColumns(reorderedColumns);
    } else {
      setColumns(originalColumns);
    }
  };

  static handleSelection = (data, stockData, selectionModel) => {
    let value;

    if (typeof stockData[0].stoneid === "string") {
      value = data;
    } else {
      value = Utility.StringToIntegerArray(data)
    }

    let newValue = new Set(selectionModel);
    let dataCertificateIds = new Map();
    let dataIds = [];
    stockData.map(iStockData => {
      dataIds.push(iStockData.stoneid);
      dataCertificateIds.set(parseInt(iStockData.certificateid), iStockData.stoneid);
      return true;
    });

    value.map(ivalue => {
      if (dataIds.includes(ivalue)) {
        newValue.add(ivalue);
      } else if (dataCertificateIds.has(ivalue)) {
        newValue.add(dataCertificateIds.get(ivalue));
      }
      return true;
    })

    let matchedList = [];
    let unmatchedList = [];
    stockData.map(iStockData => {
      if (newValue.has(iStockData.stoneid)) {
        matchedList.push(iStockData);
      } else {
        unmatchedList.push(iStockData);
      }
      return true;
    })

    return [matchedList.concat(unmatchedList), Array.from(newValue)]
  }


  static getDefaultCriteriaToGenerateLink = () => {

    let data = {
      customerOption: [],
      selectedCustomer: "",
      customerLoadSpinner: false,
      savedSearch: true,
      savedSearchOption: [],
      selectedSavedSearch: {},
      savedSearchCriteria: {},

      previousSearchOption: [],
      selectedPreviousSearch: {},

      showMoreShapeOption: true,
      showAllShape: false,
      shapeOption: [],
      selectedShape: [],

      weightSpecific: true,
      weightOption: [],
      weightFrom: '',
      weightTo: '',
      generalWeightDossiers: false,
      generalWeightCarat: false,
      generalSelectedWeight: [],

      colorWhite: true,
      colorOption: [],
      intensityOption: this.getIntensityOption(),
      selectedFromIntensity: '',
      selectedToIntensity: '',
      overtoneOption: this.getOvertoneOption(),
      selectedOvertone: '',
      fancyColorOption: this.getFancycolorOption(),
      selectedFancyColor1: '',
      selectedFancyColor2: '',

      cutOption: [],
      polOption: [],
      symOption: [],
      floOption: [],
      cpsOption: this.getCPSOption(),

      labOption: [],
      locationOption: [],

      bgmOption: this.getBGMOption(),
      blackOption: this.getBlackOption(),

      clarityOption: [],

      depthFrom: '',
      depthTo: '',
      tableFrom: '',
      tableTo: '',
      ratioFrom: '',
      ratioTo: '',
      lengthFrom: '',
      lengthTo: '',
      widthFrom: '',
      widthTo: '',
      priceFrom: '',
      priceTo: '',
      totalFrom: '',
      totalTo: '',

      showAdvanceOption: false,

      disabledSearch: true,
      showSuccessSave: false,
      showSaveAsModal: false,
      saveAsSearchName: '',
      saveAsLoading: false,

      stoneIds: ''
    };

    return data;
  }


  // Functions to get options (these should be defined elsewhere)
  static getIntensityOption = () => {
    // return intensity options
    return [
      { value: 1, label: "Faint" },
      { value: 2, label: "Very Light" },
      { value: 3, label: "Light" },
      { value: 4, label: "Fancy Light" },
      { value: 5, label: "Fancy" },
      { value: 6, label: "Fancy Dark" },
      { value: 7, label: "Fancy Intense" },
      { value: 8, label: "Fancy Vivid" },
      { value: 9, label: "Fancy Deep" },
      { value: 10, label: "Fancy Brown" }
    ];
  }

  static getOvertoneOption = () => {
    // return overtone options
    return [
      { value: 1, label: "Yellow" },
      { value: 2, label: "Yellowish" },
      { value: 3, label: "Pink" },
      { value: 4, label: "Pinkish" },
      { value: 5, label: "Blue" },
      { value: 6, label: "Bluish" },
      { value: 7, label: "Red" },
      { value: 8, label: "Redish" },
      { value: 9, label: "Green" },
      { value: 10, label: "Greenish" },
      { value: 11, label: "Purple" },
      { value: 12, label: "Purplish" },
      { value: 13, label: "Orange" },
      { value: 14, label: "Orangey" },
      { value: 15, label: "Violet" },
      { value: 16, label: "Gray" },
      { value: 17, label: "Grayish" },
      { value: 18, label: "Brown" },
      { value: 19, label: "Brownish" },
      { value: 20, label: "Other" },
      { value: 21, label: "Brown-Greenish" },
    ];
  }

  static getFancycolorOption = () => {
    // return fancy color options
    return [
      { value: 1, label: "Yellow" },
      { value: 2, label: "Pink" },
      { value: 3, label: "Blue" },
      { value: 4, label: "Red" },
      { value: 5, label: "Green" },
      { value: 6, label: "Purple" },
      { value: 7, label: "Orange" },
      { value: 8, label: "Gray" },
      { value: 9, label: "Black" },
      { value: 10, label: "Brown" },
      { value: 12, label: "Other" },
      { value: 13, label: "White" },
    ];
  }

  static getCPSOption = () => {
    // return CPS options
    return [
      { value: 1, label: "3EX", selected: false },
      { value: 2, label: "EX Cut", selected: false },
      { value: 3, label: "3VG+", selected: false },
    ];
  }

  static getBGMOption = () => {
    // return BGM options
    return [
      { value: "NO BGM", label: "NO BGM", selected: false },
      { value: "ONLY MILKY", label: "ONLY MILKY", selected: false },
      { value: "ONLY BROWN", label: "ONLY BROWN", selected: false }
    ];
  }

  static getBlackOption = () => {
    // return black options
    return [
      { value: "NO BLACK", label: "NO BLACK", selected: false },
      { value: "SIDE BLACK", label: "SIDE BLACK", selected: false }
    ];
  }

  static getFirstAndLastDate(monthYearStr) {
    // Split the input string to get the month and year
    const [monthName, year] = monthYearStr.split("-");

    // Create a Date object for the first day of the month
    const firstDate = new Date(`${monthName} 1, ${year}`);

    // Create a Date object for the last day of the month
    const lastDate = new Date(firstDate.getFullYear(), firstDate.getMonth() + 1, 0);

    // Helper function to format the date as dd-mm-yyyy
    function formatDate(date) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-based, so add 1
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }

    // Return the formatted first and last dates
    return {
      firstDate: formatDate(firstDate),
      lastDate: formatDate(lastDate)
    };
  }

  static downloadPDF = (columns, rows, pdfname) => {

    const doc = new jsPDF();
    const tableColumn = columns;
    const tableRows = rows.map(row => columns.map(col => row[col]));

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      styles: {
        font: 'helvetica',
        fontSize: 7,
        cellPadding: 1,
        halign: 'center', // Horizontal alignment
        valign: 'middle', // Vertical alignment
        lineColor: [0, 0, 0], // Black border
        lineWidth: 0.1
      },
      headStyles: {
        fillColor: [185, 185, 185],
        textColor: 20,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        lineColor: [0, 0, 0], // Ensures black border for header too
        lineWidth: 0.1
      },
      theme: 'grid',
      startY: 8,
      margin: { left: 10, right: 10 },
      tableWidth: 'auto',

      // Alternate row background color
      didParseCell: function (data) {
        if (data.section === 'body' && data.row.index % 2 !== 0) {
          data.cell.styles.fillColor = [245, 245, 245]; // light gray
        }
      }
    });

    doc.save(pdfname);
  };


}